import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Restaurant as RestaurantIcon,
  Videocam as VideocamIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminIcon,
  ChildCare as ChildIcon,
} from '@mui/icons-material';
import axios from 'axios';

interface Notification {
  _id: string;
  type: string;
  message: string;
  createdAt: string;
  read: boolean;
  resourceType?: string;
  resourceId?: string;
  createdBy?: {
    _id: string;
    name: string;
    email: string;
  };
}

const Notifications: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Налаштування axios
  const api = axios.create({
    baseURL: 'https://api.smartik.com.ua/api'
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  useEffect(() => {
    fetchNotifications();
    
    // Оновлюємо сповіщення кожні 30 секунд
    const interval = setInterval(fetchNotifications, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/notifications?limit=10');
      setNotifications(response.data.data || []);
    } catch (error: any) {
      console.error('Помилка при отриманні сповіщень:', error);
      setError('Помилка завантаження сповіщень');
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    // Оновлюємо сповіщення при кліку
    await fetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: Notification) => {
    try {
      // Позначаємо сповіщення як прочитане
      if (!notification.read) {
        await api.patch(`/notifications/${notification._id}/read`);
        setNotifications(prev => 
          prev.map(n => 
            n._id === notification._id ? { ...n, read: true } : n
          )
        );
      }

      // Закриваємо меню
      handleClose();

      // Тут можна додати навігацію до відповідного ресурсу
      // navigate(`/${notification.resourceType}/${notification.resourceId}`);
    } catch (error) {
      console.error('Помилка при обробці сповіщення:', error);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'admin_created':
      case 'admin_updated':
      case 'admin_deleted':
        return <AdminIcon fontSize="small" />;
      case 'kindergarten_created':
      case 'kindergarten_updated':
      case 'kindergarten_deleted':
        return <SchoolIcon fontSize="small" />;
      case 'child_added':
      case 'child_updated':
      case 'child_deleted':
        return <ChildIcon fontSize="small" />;
      case 'parent_added':
      case 'parent_updated':
      case 'parent_deleted':
        return <PersonIcon fontSize="small" />;
      case 'menu_updated':
      case 'menu_created':
        return <RestaurantIcon fontSize="small" />;
      case 'camera_added':
      case 'camera_updated':
        return <VideocamIcon fontSize="small" />;
      default:
        return <NotificationsIcon fontSize="small" />;
    }
  };

  const formatTimestamp = (createdAt: string) => {
    const date = new Date(createdAt);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) {
      return 'Щойно';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} хв тому`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} год тому`;
    } else {
      return date.toLocaleDateString('uk-UA');
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <>
      <IconButton color="inherit" onClick={handleClick}>
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 500,
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Останні дії
          </Typography>
        </Box>
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ m: 1 }}>
            {error}
          </Alert>
        )}
        
        {!loading && !error && notifications.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Немає нових сповіщень
            </Typography>
          </Box>
        )}
        
        {!loading && !error && notifications.length > 0 && (
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification._id}>
                <MenuItem 
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    backgroundColor: notification.read ? 'transparent' : '#f5f5f5',
                    '&:hover': {
                      backgroundColor: notification.read ? '#f5f5f5' : '#e8e8e8',
                    },
                    py: 1.5,
                  }}
                >
                  <ListItemIcon>
                    {getNotificationIcon(notification.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: notification.read ? 'normal' : 'bold',
                          color: notification.read ? 'text.secondary' : 'text.primary'
                        }}
                      >
                        {notification.message}
                      </Typography>
                    }
                                         secondary={
                       <Typography variant="caption" color="text.secondary">
                         {formatTimestamp(notification.createdAt)}
                       </Typography>
                     }
                  />
                </MenuItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </Box>
        )}
      </Menu>
    </>
  );
};

export default Notifications;
